import type { ChartSeries } from './palette.ts';
import type { ChartTheme } from './useThemeColors.ts';

/** Axis and legend text. Small, because a chart is read through its shape. */
const TICK_SIZE = 11;

/** The part of a chart.js tooltip item the label reads; shared by bar and line items. */
interface TooltipLabelItem {
  parsed: { y: number | null };
  dataset: { label?: string };
}

function font(theme: ChartTheme) {
  return { family: theme.fontFamily, size: TICK_SIZE };
}

export function formatValue(value: number, yFormat?: (value: number) => string): string {
  return yFormat ? yFormat(value) : String(value);
}

/*
 * A navy card with white text — the same object the rest of the app uses for
 * something floating over the page, drawn by hand because a canvas cannot
 * inherit it.
 */
export function tooltipOptions(theme: ChartTheme, yFormat?: (value: number) => string) {
  return {
    backgroundColor: theme.tooltipSurface,
    titleColor: theme.tooltipTitle,
    bodyColor: theme.tooltipText,
    cornerRadius: theme.tooltipRadius,
    padding: 10,
    boxPadding: 4,
    usePointStyle: true,
    titleFont: { family: theme.fontFamily, size: TICK_SIZE, weight: 'bold' as const },
    bodyFont: { family: theme.fontFamily, size: 12 },
    callbacks: {
      label: (item: TooltipLabelItem) => {
        const shown = formatValue(item.parsed.y ?? 0, yFormat);
        return item.dataset.label ? `${item.dataset.label}: ${shown}` : shown;
      },
    },
  };
}

export function legendOptions(theme: ChartTheme, display: boolean) {
  return {
    display,
    position: 'bottom' as const,
    labels: {
      color: theme.text,
      font: font(theme),
      boxWidth: 8,
      boxHeight: 8,
      usePointStyle: true,
      pointStyle: 'circle' as const,
      padding: 16,
    },
  };
}

/*
 * No vertical gridlines: the categories are already separated by the marks
 * standing on them, and a grid behind bars reads as a second set of bars.
 */
export function categoryAxis(theme: ChartTheme, stacked = false) {
  return {
    stacked,
    grid: { display: false },
    border: { color: theme.grid },
    ticks: { color: theme.text, font: font(theme), maxRotation: 0, autoSkipPadding: 12 },
  };
}

export function valueAxis(
  theme: ChartTheme,
  yFormat?: (value: number) => string,
  stacked = false,
) {
  return {
    stacked,
    beginAtZero: true,
    grid: { color: theme.grid },
    border: { display: false },
    ticks: {
      color: theme.text,
      font: font(theme),
      /* No formatter means the axis counts things, and half a person is noise. */
      precision: yFormat ? undefined : 0,
      callback: (value: string | number) => formatValue(Number(value), yFormat),
    },
  };
}

/**
 * The chart in a sentence, for the sr-only line under the canvas.
 *
 * Where it starts, where it ends and how far it swung is what someone takes
 * away from glancing at a trend line; reading every point back would be a
 * table, and a table is a different component.
 */
export function chartSummary(
  ariaLabel: string,
  series: ChartSeries[],
  labels: string[],
  yFormat?: (value: number) => string,
): string {
  const span =
    labels.length > 1
      ? `${labels.length} points from ${labels[0]} to ${labels[labels.length - 1]}`
      : `${labels.length} point`;

  const lines = series.map((one) => {
    const values = one.data.filter((value) => Number.isFinite(value));
    if (values.length === 0) return `${one.label}: no values`;

    const first = formatValue(values[0], yFormat);
    const last = formatValue(values[values.length - 1], yFormat);
    const high = formatValue(Math.max(...values), yFormat);
    const low = formatValue(Math.min(...values), yFormat);

    return `${one.label}: ${first} to ${last}, high ${high}, low ${low}`;
  });

  return `${ariaLabel}. ${span}. ${lines.join('. ')}.`;
}
